import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useSEO, createOrganizationSchema, createWebPageSchema } from '../../utils/seo';

export default function HomePage() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useSEO({
    title: 'AXIOM Kalite Kontrol ve İnsan Kaynakları Danışmanlığı',
    description: 'AXIOM olarak kalite kontrol ve insan kaynakları alanında profesyonel danışmanlık hizmetleri sunuyoruz. ISO standartları, süreç optimizasyonu ve daha fazlası.',
    keywords: 'kalite kontrol, insan kaynakları, danışmanlık, ISO standartları, süreç optimizasyonu',
    canonical: import.meta.env.VITE_SITE_URL || 'https://example.com',
    schema: {
      '@graph': [
        createOrganizationSchema(),
        createWebPageSchema(
          'AXIOM Kalite Kontrol ve İnsan Kaynakları Danışmanlığı',
          'AXIOM olarak kalite kontrol ve insan kaynakları alanında profesyonel danışmanlık hizmetleri sunuyoruz.'
        )
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header>
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm shadow-sm'}`}>
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3 z-10">
                <img src="https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/edcc2fca5481151c158722794d786f43.png" alt="AXIOM Logo" className="h-12 w-auto object-contain" />
              </Link>
              
              {/* Desktop Menu */}
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm font-semibold text-gray-900 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_home')}</Link>
                <Link to="/about" className="text-sm font-semibold text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_about')}</Link>
                <Link to="/services" className="text-sm font-semibold text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_services')}</Link>
                <Link to="/training" className="text-sm font-semibold text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_training')}</Link>
                <Link to="/blog" className="text-sm font-semibold text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_blog')}</Link>
                <Link to="/career" className="text-sm font-semibold text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">Kariyer</Link>
                <Link to="/contact" className="bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer">{t('nav_contact')}</Link>
                <LanguageSwitcher />
              </div>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
              <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
                <div className="flex flex-col gap-2">
                  <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-semibold text-gray-900 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_home')}</Link>
                  <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-semibold text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_about')}</Link>
                  <Link to="/services" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-semibold text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_services')}</Link>
                  <Link to="/training" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-semibold text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_training')}</Link>
                  <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-semibold text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_blog')}</Link>
                  <Link to="/career" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-semibold text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">Kariyer</Link>
                  <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="bg-teal-600 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-teal-700 transition-colors text-center whitespace-nowrap cursor-pointer">{t('nav_contact')}</Link>
                  <div className="px-3 py-2">
                    <LanguageSwitcher />
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center pt-20 pb-24 overflow-hidden">
          {/* Video Background */}
          <div className="absolute inset-0 z-0">
            <iframe
              className="w-full h-full object-cover pointer-events-none"
              src="https://www.youtube.com/embed/B2PC44i4D60?autoplay=1&mute=1&loop=1&playlist=B2PC44i4D60&controls=0&showinfo=0&rel=0&modestbranding=1&playsinline=1"
              title="Hero Background Video"
              allow="autoplay; encrypted-media"
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: '100vw',
                height: '100vh',
                transform: 'translate(-50%, -50%)',
                minWidth: '100%',
                minHeight: '100%',
                objectFit: 'cover'
              }}
            />
            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70"></div>
          </div>

          {/* Animated Gradient Orbs */}
          <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
            <div 
              className="absolute w-96 h-96 bg-teal-500/20 rounded-full blur-3xl animate-pulse"
              style={{
                top: '20%',
                left: '10%'
              }}
            ></div>
            <div 
              className="absolute w-96 h-96 bg-emerald-500/15 rounded-full blur-3xl animate-pulse"
              style={{
                bottom: '20%',
                right: '10%',
                animationDelay: '1s'
              }}
            ></div>
          </div>
          
          <div className="relative z-10 max-w-7xl mx-auto px-6 w-full text-center">
            <div className="max-w-4xl mx-auto">
              {/* Animated Badge */}
              <div className="inline-flex items-center gap-2 bg-teal-600/20 backdrop-blur-sm rounded-full px-5 py-2.5 mb-8 border border-teal-500/30 animate-fade-in-down">
                <i className="ri-award-line text-teal-400 text-base animate-pulse"></i>
                <span className="text-teal-300 text-sm font-semibold">Kaliteyi ölçmek için değil, Yaşatmak için buradayız.</span>
              </div>
              
              {/* AXIOM Large Text */}
              <div className="mb-6 animate-fade-in" style={{ animationDelay: '0.1s' }}>
                <h2 className="text-8xl md:text-9xl font-bold text-white tracking-wider">
                  AXIOM
                </h2>
              </div>
              
              {/* Animated Title */}
              <h1 className="text-2xl md:text-3xl font-bold mb-8 leading-tight text-white animate-fade-in-up">
                Kalite Kontrol ve İnsan Kaynakları Danışmanlığı
              </h1>
              
              {/* Animated Description */}
              <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto animate-fade-in" style={{ animationDelay: '0.6s' }}>
                Otomotiv sektöründe kalite kontrol, muayene ve insan kaynakları hizmetlerinde uzman çözüm ortağınız. ISO standartlarında profesyonel destek.
              </p>
              
              {/* Animated Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
                <Link to="/contact" className="bg-teal-600 text-white px-10 py-4 rounded-full text-base font-semibold hover:bg-teal-700 transition-all hover:scale-105 shadow-xl hover:shadow-2xl whitespace-nowrap cursor-pointer transform hover:-translate-y-1">
                  Hemen Teklif Alın
                </Link>
                <Link to="/services" className="bg-white/10 backdrop-blur-sm text-white px-10 py-4 rounded-full text-base font-semibold hover:bg-white/20 transition-all border-2 border-white/30 hover:border-white/50 whitespace-nowrap cursor-pointer transform hover:-translate-y-1">
                  Hizmetlerimizi Keşfedin
                </Link>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
              <div className="w-1 h-2 bg-white/50 rounded-full animate-scroll"></div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <img src="https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/723dc54c55285e324fa54e72204552e6.jpeg" alt="AXIOM Logo" className="h-10 w-10 object-contain" />
                <span className="text-2xl font-bold">AXIOM</span>
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                Otomotiv sektöründe kalite kontrol ve insan kaynakları alanında 15 yılı aşkın tecrübemizle, müşterilerimize en yüksek standartlarda hizmet sunuyoruz.
              </p>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/axiom-kalite" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="https://x.com/AxiomKalite" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-lg"></i>
                </a>
                <a href="https://www.instagram.com/axiomkalite/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
                <a href="https://www.facebook.com/profile.php?id=61585433974202" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">Hizmetler</h4>
              <ul className="space-y-3">
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Kalite Kontrol</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Muayene Hizmetleri</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">İnsan Kaynakları</Link></li>
                <li><Link to="/training" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Eğitim</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">Kurumsal</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Hakkımızda</Link></li>
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Blog</Link></li>
                <li><Link to="/career" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">Kariyer</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">İletişim</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">İletişim</h4>
              <ul className="space-y-3">
                <li className="text-gray-400 text-sm">Maslak Mahallesi<br />Büyükdere Caddesi<br />İstanbul</li>
                <li><a href="tel:+902125552300" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">+90 (555) 820 46 46</a></li>
                <li><a href="mailto:info@axiomkalite.com" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">info@axiomkalite.com</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
              <p>&copy; 2025 AXIOM. Tüm hakları saklıdır.</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
